import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Building2 } from "lucide-react";
import type { ReactNode } from "react";
import type { Company } from "@/companies/company";
import {
  companyAddressLines,
  formatCompanyPhone,
} from "@/companies/companyFields";
import { DEFAULT_COMPANIES_SEARCH } from "@/companies/companiesSearch";
import { Button, Card, CardContent, Skeleton } from "@rumbledirect/ui-v2";
import { COMPANY_TYPE_LABELS } from "@/dossiers/companyLabels";
import { DetailSection, FieldGrid } from "@/dossiers/DetailSection";
import { useGetAdminCompaniesCompanyId } from "@/lib/api/generated/company/company";

export const Route = createFileRoute("/_authenticated/companies_/$companyId")({
  component: CompanyDetailPage,
});

function CompanyDetailPage(): ReactNode {
  const { companyId } = Route.useParams();
  const { data, isPending, isError, refetch } =
    useGetAdminCompaniesCompanyId(companyId);

  return (
    <div className="space-y-4">
      <Link
        to="/companies"
        search={DEFAULT_COMPANIES_SEARCH}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Terug naar opdrachtgevers
      </Link>

      {isError ? (
        <Card role="alert">
          <CardContent className="py-10 text-center">
            <div className="space-y-3">
              <p className="text-muted-foreground">
                We konden deze opdrachtgever niet laden.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
              >
                Opnieuw proberen
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <CompanyDetailHeader company={data} isPending={isPending} />
          <CompanyDetailBody company={data} isPending={isPending} />
        </>
      )}
    </div>
  );
}

function CompanyDetailHeader({
  company,
  isPending,
}: {
  readonly company: Company | undefined;
  readonly isPending: boolean;
}): ReactNode {
  if (isPending || !company) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label="Opdrachtgever laden"
      >
        <Skeleton className="h-9 w-64" />
      </div>
    );
  }

  const typeLabel = COMPANY_TYPE_LABELS[company.type];

  return (
    <div className="space-y-1">
      <h1 className="text-2xl font-semibold tracking-tight">
        {company.fullName}
      </h1>
      <p className="text-sm text-muted-foreground">{typeLabel}</p>
    </div>
  );
}

function CompanyDetailBody({
  company,
  isPending,
}: {
  readonly company: Company | undefined;
  readonly isPending: boolean;
}): ReactNode {
  if (isPending || !company) {
    return (
      <Card>
        <CardContent className="space-y-3 py-6">
          {["a", "b", "c", "d"].map((key) => (
            <Skeleton key={key} className="h-4 w-full max-w-md" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const addressLines = companyAddressLines(company.address);

  // One card, not three: the resource carries at most ten fields and `FieldGrid`
  // drops the empty ones, so splitting them across Algemeen/Contact/Adres cards
  // spent three headers and three `Reveal` animations on a single screenful.
  // The address is one field holding a postal block, so it reads as an address
  // instead of four unrelated cells; it is absent entirely (not blank) when the
  // endpoint omits it, which it does all-or-nothing.
  return (
    <DetailSection title="Algemeen" icon={Building2}>
      <FieldGrid
        fields={[
          { label: "Naam", value: company.name },
          { label: "Volledige naam", value: company.fullName },
          {
            label: "Type",
            value: COMPANY_TYPE_LABELS[company.type],
          },
          { label: "Groep", value: company.groupName ?? undefined },
          { label: "E-mailadres", value: company.email ?? undefined },
          {
            label: "Telefoonnummer",
            value: formatCompanyPhone(company.telephone),
          },
          {
            label: "Adres",
            value:
              addressLines.length === 0 ? undefined : (
                <span className="flex flex-col">
                  {addressLines.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </span>
              ),
          },
        ]}
      />
    </DetailSection>
  );
}
